import { createContext, useContext, useState } from 'react'

import {
  CreateIntegrationsSyncRunParametersInput,
  CreateSyncRunDocument,
  CreateSyncRunMutation,
  IntegrationsCapabilityModeEnum,
  IntegrationsSyncRunFragment,
  IntegrationsSyncRunOriginEnum,
  LastSyncRunDocument,
  MarketplaceDbIntegration,
} from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { GraphqlDataCollectionMappedVariables } from 'modules/core/hooks/useGraphqlDataCollectionSource'
import { useMutation } from 'modules/core/hooks/useMutation'
import { useQuery } from 'modules/core/hooks/useQuery'

import { SyncableStateAggregation } from 'modules/integrations/components/SyncFooter/LastSync/Syncing/StatusAggregation'
import { useAvailableIntegration } from 'modules/integrations/hooks/SyncContext/availableIntegration'
import { useSyncRunStatusChangedSubscription } from 'modules/integrations/hooks/SyncContext/subscriptions/useSyncRunStatusChangedSubscription'
import { useSyncableStateUpdatedSubscription } from 'modules/integrations/hooks/SyncContext/subscriptions/useSyncableStateUpdatedSubscription'
import { SyncRunStatus } from 'modules/integrations/lib/syncRun'

// Accept any GraphqlDataCollectionSource variant regardless of type parameters
// since we only use mappedVariables which is common to all variants
export type AnyGraphqlDataCollectionSource = {
  mappedVariables?: GraphqlDataCollectionMappedVariables
  [key: string]: unknown
}

export interface SyncParameter {
  key: string
  value: string
}

export interface DataCollectionMappedVariable {
  key: string
  value: string
}

export type ContextParameters = Record<string, string>

export type DataCollectionMappedVariables = DataCollectionMappedVariable[]

export interface Configuration {
  origin: IntegrationsSyncRunOriginEnum
  syncableStatesAggregation?: SyncableStateAggregation
}

export interface SyncContextType {
  syncLoading: boolean
  isSyncRunning: boolean
  setContextParameters: (parameters: ContextParameters) => void
  marketplaceIntegration: MarketplaceDbIntegration | null
  syncEnabled: boolean
  lastSyncRun?: IntegrationsSyncRunFragment | null
  setDataCollectionSource: (source: AnyGraphqlDataCollectionSource) => void
}

export interface PrivateSyncContextType {
  runSynchronisation: () => Promise<void>
  syncLoading: boolean
  isSyncRunning: boolean
  contextParameters: ContextParameters
  marketplaceIntegration: MarketplaceDbIntegration | null
  syncEnabled: boolean
  lastSyncRun?: IntegrationsSyncRunFragment | null
  getDataCollectionSource: () => AnyGraphqlDataCollectionSource | null
  configuration: Configuration
  capabilityMode?: IntegrationsCapabilityModeEnum | null
}

const SyncContext = createContext<SyncContextType | null>(null)
const PrivateSyncContext = createContext<PrivateSyncContextType | null>(null)

let dataCollectionSource: AnyGraphqlDataCollectionSource | null = null

const setDataCollectionSource = (source: AnyGraphqlDataCollectionSource) => {
  dataCollectionSource = source
}

export const IntegrationsSyncContextProvider = ({
  children,
  configuration,
}: {
  children: React.ReactNode
  configuration: Configuration
}) => {
  const {
    company: { id: companyId },
  } = useCurrentSession()

  const [contextParameters, setContextParameters] = useState<ContextParameters>({})

  const contextParametersToArray = (params: ContextParameters) => {
    return Object.entries(params).map(([key, value]) => ({ key, value }))
  }

  const availableIntegration = useAvailableIntegration(configuration.origin)

  const marketplaceIntegration = availableIntegration?.marketplaceIntegration ?? null
  const capabilityMode = availableIntegration?.capabilityMode ?? null

  const syncEnabled = !!marketplaceIntegration

  const [sync, { loading: syncLoading }] = useMutation(CreateSyncRunDocument, {
    extractorFn: (data: CreateSyncRunMutation) => {
      return data.integrationsMutations.createIntegrationsSyncRun
    },
    update(cache, { data }) {
      const syncRun = data?.integrationsMutations?.createIntegrationsSyncRun?.syncRun
      if (!syncRun || !marketplaceIntegration) return

      const parameters = contextParametersToArray(contextParameters)
      // Update the LastSyncRun query cache with the new created sync run
      // instead refetching to avoid DBs replication lag issues
      cache.writeQuery({
        query: LastSyncRunDocument,
        variables: {
          marketplaceIntegrationId: marketplaceIntegration.id,
          parameters,
        },
        data: {
          integrations: {
            syncRunsConnection: {
              nodes: [syncRun],
              __typename: 'IntegrationsSyncRunConnection',
            },
            __typename: 'integrations',
          },
        },
      })
    },
  })

  const convertMappedVariablesToParameters = (
    mappedVariables: GraphqlDataCollectionMappedVariables
  ): CreateIntegrationsSyncRunParametersInput[] => {
    if (!mappedVariables) return []

    return Object.entries(mappedVariables)
      .map(
        ([key, value]: [string, unknown]): CreateIntegrationsSyncRunParametersInput | undefined => {
          let stringValue: string | undefined

          if (Array.isArray(value) && value.length > 0) {
            stringValue = value.join(',')
          } else if (['string', 'number', 'boolean'].includes(typeof value)) {
            stringValue = String(value)
          }

          if (!stringValue) return undefined

          const snakeCaseKey = key
            .replace(/([A-Z])/g, '_$1')
            .toLowerCase()
            .replace(/^_/, '')

          return {
            key: snakeCaseKey,
            value: stringValue,
          }
        }
      )
      .filter((param): param is CreateIntegrationsSyncRunParametersInput => param !== undefined)
  }

  useSyncRunStatusChangedSubscription({
    marketplaceIntegration,
    syncEnabled,
    parameters: contextParametersToArray(contextParameters),
  })

  useSyncableStateUpdatedSubscription({
    marketplaceIntegration,
    syncEnabled,
  })

  const runSynchronisation = async () => {
    if (!syncEnabled || !marketplaceIntegration) return

    await sync({
      variables: {
        companyId,
        marketplaceIntegrationId: marketplaceIntegration.id,
        origin: configuration.origin,
        parameters: convertMappedVariablesToParameters(dataCollectionSource?.mappedVariables),
      },
    })
  }

  const { data: syncRunData } = useQuery(LastSyncRunDocument, {
    variables: {
      marketplaceIntegrationId: marketplaceIntegration?.id || 0,
      parameters: contextParametersToArray(contextParameters),
    },
    skip: !syncEnabled,
  })

  const lastSyncRun = syncRunData?.integrations?.syncRunsConnection.nodes[0] || null

  const getDataCollectionSource = () => {
    return dataCollectionSource
  }

  const isSyncRunning = syncLoading || lastSyncRun?.status === SyncRunStatus.RUNNING

  return (
    <PrivateSyncContext.Provider
      value={{
        runSynchronisation,
        syncLoading,
        isSyncRunning,
        contextParameters,
        marketplaceIntegration,
        syncEnabled,
        lastSyncRun,
        getDataCollectionSource,
        configuration,
        capabilityMode,
      }}
    >
      <SyncContext.Provider
        value={{
          syncLoading,
          isSyncRunning,
          setContextParameters,
          marketplaceIntegration,
          syncEnabled,
          lastSyncRun,
          setDataCollectionSource,
        }}
      >
        {children}
      </SyncContext.Provider>
    </PrivateSyncContext.Provider>
  )
}

export function useIntegrationsSyncContext() {
  const context = useContext(SyncContext)

  if (!context) {
    throw new Error('useIntegrationsSyncContext must be used within a SyncContextProvider')
  }

  return context
}

export function usePrivateIntegrationsSyncContext() {
  const context = useContext(PrivateSyncContext)

  if (!context) {
    throw new Error(
      'usePrivateIntegrationsSyncContext must be used within a PrivateSyncContextProvider'
    )
  }

  return context
}
